import { useNavigation } from "@react-navigation/native";
import { useCallback, useMemo } from "react";

import { Callout, CalloutRefresh } from "../components";
import { UserValidator, useCities, useConfig, User } from "../services";
import { LoadingScreen } from "./loading";

type Props = {
  loading?: boolean;
  user: User;
};

export function Onboard({ loading = false, user }: Props) {
  // Data
  const {
    data: cities,
    isLoading: isCitiesLoading,
    refetch: refetchCities,
  } = useCities();
  const {
    data: config,
    isLoading: isConfigLoading,
    refetch: refetchConfig,
  } = useConfig();

  // Vars
  const navigation = useNavigation();
  const city = useMemo(() => {
    return cities?.find((_city) => _city.id === user.city?.id) ?? null;
  }, [cities, user]);

  // Methods
  const refresh = useCallback(() => {
    refetchConfig();
    refetchCities();
  }, [refetchConfig, refetchCities]);

  const goToIdentity = useCallback(() => {
    navigation.navigate("Identity");
  }, [navigation]);

  const goToBody = useCallback(() => {
    navigation.navigate("Body");
  }, [navigation]);

  const goToLanguages = useCallback(() => {
    navigation.navigate("Languages");
  }, [navigation]);

  const goToServices = useCallback(() => {
    navigation.navigate("Services");
  }, [navigation]);

  const goToRates = useCallback(() => {
    navigation.navigate("Rates");
  }, [navigation]);

  const goToPhotos = useCallback(() => {
    navigation.navigate("Photos");
  }, [navigation]);

  const goToSocials = useCallback(() => {
    navigation.navigate("Socials");
  }, [navigation]);

  const goToIncall = useCallback(() => {
    navigation.navigate("Incall");
  }, [navigation]);

  const goToCityAreas = useCallback(() => {
    navigation.navigate("CityAreas");
  }, [navigation]);

  const goToVerifyIdentity = useCallback(() => {
    navigation.navigate("VerifyIdentity");
  }, [navigation]);

  // Render
  if (isConfigLoading || isCitiesLoading) {
    return <LoadingScreen />;
  }

  if (!config) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  if (!UserValidator.identity(user)) {
    return (
      <Callout
        icon="fingerprint"
        title="Setup profile"
        message="You need to complete your profile before you can go online."
        onPress={goToIdentity}
        action="Update profile"
        loading={loading}
      />
    );
  }

  if (!UserValidator.body(user)) {
    return (
      <Callout
        icon="alert"
        title="Setup measurements"
        message="You need to complete your measurements before you can go online."
        onPress={goToBody}
        action="Update measurements"
        loading={loading}
      />
    );
  }

  if (!UserValidator.languages(user)) {
    return (
      <Callout
        icon="translate"
        title="Setup languages"
        message="You need to add language(s) before you can go online."
        onPress={goToLanguages}
        action="Update languages"
        loading={loading}
      />
    );
  }

  if (!UserValidator.services(user)) {
    return (
      <Callout
        icon="medical-bag"
        title="Setup services"
        message="You need to add service(s) before you can go online."
        onPress={goToServices}
        action="Update services"
        loading={loading}
      />
    );
  }

  if (!UserValidator.rates(user)) {
    return (
      <Callout
        icon="cash-multiple"
        title="Setup rates"
        message="You need to add rate(s) before you can go online."
        onPress={goToRates}
        action="Update rates"
        loading={loading}
      />
    );
  }

  if (!UserValidator.incall(user, config)) {
    return (
      <Callout
        icon="city"
        title="Setup incall location"
        message="You need to add an incall location before you can go online."
        onPress={goToIncall}
        action="Update location"
        loading={loading}
      />
    );
  }

  if (!UserValidator.images(user)) {
    return (
      <Callout
        icon="image"
        title="Setup photos"
        message="You need to upload at least 4 photo(s) before you can go online."
        onPress={goToPhotos}
        action="Upload photos"
        loading={loading}
      />
    );
  }

  if (!UserValidator.socials(user)) {
    return (
      <Callout
        icon="account-group"
        title="Setup social links"
        message="You need to add social links before you can go online."
        onPress={goToSocials}
        action="Update social links"
        loading={loading}
      />
    );
  }

  if (!UserValidator.cityAreas(user, city)) {
    return (
      <Callout
        icon="map-marker"
        title="Setup city areas"
        message="You need to select at least one city area before you can go online."
        onPress={goToCityAreas}
        action="Update city areas"
        loading={loading}
      />
    );
  }

  if (!UserValidator.verified(user)) {
    return (
      <Callout
        icon="fingerprint"
        title="Verify identity"
        message="You need to verify your identity before you can go online."
        onPress={goToVerifyIdentity}
        action="Upload ID"
        loading={loading}
      />
    );
  }

  return (
    <Callout
      icon="face-agent"
      title="Contact us"
      message="There is a problem with your profile. Please contact us at contact@smooci.com or on Telegram @SmoociSammy"
    />
  );
}
